import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CancelIcon from '@mui/icons-material/Cancel';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  verifyIngenicoPaymentAfterCommError,
  voidIngenicoOpenTransaction,
  IngenicoPaymentVerification,
} from '../../hooks/fiscalDevices/ingenico';

interface PosPaymentErrorModalProps {
  open: boolean;
  errorMessage: string;
  onRetry: () => void;
  onClose: () => void;
  onCloseAndClearCart: () => void;
  isRestaurantMode?: boolean;
  selectedFiscalDevice?: string | null;
  onCancelDocument?: () => Promise<boolean>;
  /**
   * ✅ YENİ (Ingenico): "Yine de Devam Et" — ödeme cihazdan gerçekte alınmış ama sistem
   * hata sanıyorsa kasiyer satışı yazarkasasız sisteme kaydedebilir (gün sonu tutarlılığı).
   */
  onContinueAnyway?: () => void;
}

const PosPaymentErrorModal: React.FC<PosPaymentErrorModalProps> = ({
  open,
  errorMessage,
  onRetry,
  onClose,
  onCloseAndClearCart,
  isRestaurantMode = false,
  selectedFiscalDevice,
  onCancelDocument,
  onContinueAnyway,
}) => {
  const [isCancellingDocument, setIsCancellingDocument] = useState(false);
  const [cancelDocumentResult, setCancelDocumentResult] = useState<string | null>(null);

  // ✅ YENİ (Ingenico): GetTicket ile cihaz durumu sorgulama
  const [isCheckingDevice, setIsCheckingDevice] = useState(false);
  const [deviceCheckResult, setDeviceCheckResult] = useState<IngenicoPaymentVerification | null>(null);
  // ✅ 'none' durumunda kullanıcı onayıyla fiş iptali
  const [isVoidingTicket, setIsVoidingTicket] = useState(false);
  const [voidTicketResult, setVoidTicketResult] = useState<'success' | 'error' | null>(null);

  const isIngenico = selectedFiscalDevice === 'ingenico_5000f';

  // Modal kapandığında sorgu sonucunu sıfırla (bir sonraki hata için temiz başla)
  useEffect(() => {
    if (!open) {
      setDeviceCheckResult(null);
      setIsCheckingDevice(false);
      setIsVoidingTicket(false);
      setVoidTicketResult(null);
    }
  }, [open]);

  // ✅ YENİ (Ingenico): Cihaz durumunu GetTicket ile sorgula.
  // Doküman akışı (PaymentTimeout/DLL Reset): GetTicket sonrası karar UYGULAMAYA bırakılır.
  // - completed: ödeme alınmış; açık fiş varsa kapatılır → "Ödemeyi Kaydet" gösterilir
  // - none:     fişte ödeme görünmüyor → OTOMATİK İPTAL YOK, kullanıcı "Fişi İptal Et" ile karar verir
  // - partial:  kısmi ödeme var → tutarlar gösterilir, kullanıcı cihazdan tamamlamalı/iptal etmeli
  // - unknown:  açık işlem yok / doğrulanamadı → POS slibine göre "Yine de Devam Et"
  const handleCheckDevice = async () => {
    setIsCheckingDevice(true);
    setDeviceCheckResult(null);
    setVoidTicketResult(null);
    try {
      const verification = await verifyIngenicoPaymentAfterCommError();
      setDeviceCheckResult(verification);
    } catch {
      setDeviceCheckResult({ status: 'unknown' });
    } finally {
      setIsCheckingDevice(false);
    }
  };

  // ✅ 'none' sonrası kullanıcı onayıyla fişi iptal et, ardından güvenle yeniden denenebilir
  const handleVoidTicket = async () => {
    setIsVoidingTicket(true);
    setVoidTicketResult(null);
    try {
      const ok = await voidIngenicoOpenTransaction();
      setVoidTicketResult(ok ? 'success' : 'error');
    } catch {
      setVoidTicketResult('error');
    } finally {
      setIsVoidingTicket(false);
    }
  };
  const isBekoX30tr = selectedFiscalDevice === 'beko_x30tr';
  const isPaperOutError = errorMessage.includes('kağıt') || errorMessage.includes('Kağıt') || errorMessage.includes('PAPER') || errorMessage.includes('paper');
  const isBekoTimeoutOrScreen9 =
    isBekoX30tr &&
    (errorMessage.includes('zaman aşımı') ||
      errorMessage.includes('zaman asimi') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('Timeout') ||
      errorMessage.toLowerCase().includes('satış ekranı açık değil') ||
      errorMessage.toLowerCase().includes('satis ekrani acik degil'));

  const handleCancelDocument = async () => {
    if (!onCancelDocument) return;
    setIsCancellingDocument(true);
    setCancelDocumentResult(null);
    try {
      const success = await onCancelDocument();
      setCancelDocumentResult(success ? 'success' : 'error');
    } catch {
      setCancelDocumentResult('error');
    } finally {
      setIsCancellingDocument(false);
    }
  };

  const handleClose = () => {
    setCancelDocumentResult(null);
    onClose();
  };

  const handleCloseAndClearCart = () => {
    setCancelDocumentResult(null);
    onCloseAndClearCart();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'error.main',
          color: 'white',
          py: 2,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 40 }} />
        <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
          Ödeme Alınamadı
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        <Alert severity="error" sx={{ mb: 2, fontSize: 16 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {errorMessage}
          </Typography>
        </Alert>

        {isIngenico && isPaperOutError && (
          <Alert severity="warning" icon={false} sx={{ mb: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Yapmanız gerekenler:
            </Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2, mb: 0 }}>
              <li>Yazarkasanın kağıt kapağını açın</li>
              <li>Yeni kağıt rulosunu takın</li>
              <li>Kapağı kapatın ve "Yeniden Dene" butonuna basın</li>
            </Typography>
          </Alert>
        )}

        {isBekoTimeoutOrScreen9 && (
          <Alert severity="warning" icon={false} sx={{ mb: 2, bgcolor: '#fff8e1' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Beko X30TR – Önemli: Çift satışı önlemek için
            </Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2, mb: 0 }}>
              <li><strong>Önce cihazı kontrol edin:</strong> Ödeme Al ekranındaysanız sol üstten &quot;Fişi İptal Et&quot; ile iptal edin.</li>
              <li>Parçalı ödeme ekranına geçtiyse tüm tutarı tamamlayın veya cihazı yeniden başlatın.</li>
              <li><strong>Sonra</strong> &quot;Yeniden Dene&quot; ile tekrar gönderin. Aksi halde çift satış oluşabilir.</li>
            </Typography>
          </Alert>
        )}

        {isIngenico && !isPaperOutError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Cihazda açık kalmış bir belge olabilir. 
              "Belgeyi İptal Et" butonuna basarak cihazda takılı kalan işlemi iptal edebilirsiniz.
            </Typography>
          </Alert>
        )}

        {cancelDocumentResult === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Cihazda açık belge başarıyla iptal edildi.
          </Alert>
        )}

        {cancelDocumentResult === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Belge iptal edilemedi. Cihazı kontrol edin.
          </Alert>
        )}

        {/* ✅ YENİ (Ingenico): GetTicket cihaz durumu sorgu sonucu */}
        {deviceCheckResult?.status === 'completed' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ✅ Ödeme cihazda ALINMIŞ ({(deviceCheckResult.totalPaymentTL ?? 0).toFixed(2)} TL) ve fiş kapatıldı.
            </Typography>
            <Typography variant="body2">
              Aşağıdaki "Ödemeyi Kaydet" butonu ile satışı sisteme işleyebilirsiniz. Yeni fiş KESİLMEZ.
            </Typography>
          </Alert>
        )}
        {deviceCheckResult?.status === 'partial' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ⚠️ Cihazda KISMİ ödeme var: {(deviceCheckResult.totalPaymentTL ?? 0).toFixed(2)} TL alınmış,
              {' '}{(deviceCheckResult.remainingAmountTL ?? 0).toFixed(2)} TL eksik.
            </Typography>
            <Typography variant="body2">
              Kalan tutarı cihazdan tamamlayın veya işlemi cihazdan iptal edin, ardından tekrar sorgulayın.
            </Typography>
          </Alert>
        )}
        {deviceCheckResult?.status === 'none' && voidTicketResult === null && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ⚠️ Fişte henüz ödeme GÖRÜNMÜYOR ({(deviceCheckResult.totalAmountTL ?? 0).toFixed(2)} TL açık fiş var).
              Fişe DOKUNULMADI.
            </Typography>
            <Typography variant="body2">
              Cihaz banka ekranında ödeme bekliyor olabilir — o durumda ödeme fişe henüz yansımamıştır.
              Lütfen <strong>cihaz ekranını kontrol edin</strong>:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>Ödeme cihazda devam ediyorsa → tamamlanmasını bekleyip tekrar "Ödemeyi Kontrol Et" basın.</li>
              <li>Ödeme kesinlikle alınmadıysa → "Fişi İptal Et ve Yeniden Dene" ile güvenle tekrar deneyin.</li>
            </Typography>
          </Alert>
        )}
        {voidTicketResult === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ✅ Açık fiş iptal edildi. "Yeniden Dene" ile güvenle tekrar deneyebilirsiniz.
            </Typography>
          </Alert>
        )}
        {voidTicketResult === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ❌ Fiş iptal edilemedi. Cihaz meşgul olabilir — cihazdaki işlemi sonuçlandırıp tekrar deneyin.
            </Typography>
          </Alert>
        )}
        {deviceCheckResult?.status === 'unknown' && deviceCheckResult?.hasOpenTransaction && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ⚠️ Cihazda AÇIK İŞLEM var ancak detayları okunamadı.
            </Typography>
            <Typography variant="body2">
              Cihaz büyük olasılıkla ödeme (banka/EFT) ekranında meşgul. Cihazdaki işlemi
              sonuçlandırın (onaylayın veya iptal edin), ardından tekrar "Ödemeyi Kontrol Et" basın.
              Fişe dokunulmadı.
            </Typography>
          </Alert>
        )}
        {deviceCheckResult?.status === 'unknown' && !deviceCheckResult?.hasOpenTransaction && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ⚠️ Cihazda açık işlem bulunamadı — durum kesin doğrulanamadı.
            </Typography>
            <Typography variant="body2">
              Ödeme alınıp fiş kapanmış OLABİLİR ya da işlem hiç başlamamış olabilir.
              Lütfen POS slibini / cihaz ekranını kontrol edin: slip çıktıysa "Yine de Devam Et",
              çıkmadıysa "Yeniden Dene" kullanın.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Lütfen aşağıdaki seçeneklerden birini seçin:
          </Typography>
          <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, mt: 1 }}>
            {isIngenico && (
              <li><strong>Ödemeyi Kontrol Et:</strong> Cihazdan gerçek ödeme durumunu sorgular (GetTicket) — ödeme alındıysa fişi kapatır ve satışı kaydetmenizi sağlar</li>
            )}
            {isIngenico && !isPaperOutError && (
              <li><strong>Belgeyi İptal Et:</strong> Cihazda takılı kalan açık belgeyi iptal eder</li>
            )}
            <li><strong>Yeniden Dene:</strong> {isPaperOutError ? 'Kağıt taktıktan sonra işlemi tekrar başlatır' : 'Müşteri farklı kart sunacaksa veya işlemi tekrar denemek istiyorsanız'}</li>
            {onContinueAnyway && (
              <li><strong>Yine de Devam Et:</strong> Ödeme cihazdan ALINDIYSA (POS slibi çıktıysa) satışı sisteme kaydeder</li>
            )}
            <li><strong>Kapat:</strong> Sepet korunacak, yeni ürün ekleyebilirsiniz</li>
            {!isRestaurantMode && (
              <li><strong>Sepeti Temizle:</strong> Müşteri vazgeçtiyse, sepet sıfırlanacak</li>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, flexDirection: 'column' }}>
        {/* ✅ YENİ (Ingenico): GetTicket ile cihaz durumunu sorgula */}
        {isIngenico && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={isCheckingDevice ? <CircularProgress size={20} color="inherit" /> : <TravelExploreIcon />}
            onClick={handleCheckDevice}
            disabled={isCheckingDevice || isCancellingDocument}
            sx={{
              bgcolor: 'info.main',
              '&:hover': { bgcolor: 'info.dark' },
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {isCheckingDevice ? 'Cihaz Sorgulanıyor (GetTicket)...' : 'Ödemeyi Kontrol Et (Cihaz Durumunu Sorgula)'}
          </Button>
        )}

        {/* ✅ 'none': fişte ödeme görünmüyor → kullanıcı ONAYIYLA iptal (otomatik iptal YOK) */}
        {deviceCheckResult?.status === 'none' && voidTicketResult !== 'success' && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={isVoidingTicket ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            onClick={handleVoidTicket}
            disabled={isVoidingTicket || isCheckingDevice || isCancellingDocument}
            sx={{
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              '&:hover': { bgcolor: 'warning.dark' },
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {isVoidingTicket ? 'Fiş İptal Ediliyor...' : 'Fişi İptal Et ve Yeniden Dene (Ödeme Alınmadıysa)'}
          </Button>
        )}

        {/* ✅ YENİ (Ingenico): Sorgu "ödeme alınmış" derse tek tuşla satışı kaydet */}
        {deviceCheckResult?.status === 'completed' && onContinueAnyway && (
          <Button
            fullWidth
            variant="contained"
            color="success"
            size="large"
            onClick={() => {
              onContinueAnyway();
            }}
            disabled={isCheckingDevice || isCancellingDocument}
            sx={{
              py: 1.5,
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            ✅ Ödemeyi Kaydet (Cihazda Alındı Doğrulandı)
          </Button>
        )}

        {/* ✅ YENİ: Yine de Devam Et — ödeme cihazda alınmışsa satışı kaybetme */}
        {onContinueAnyway && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              onContinueAnyway();
            }}
            disabled={isCancellingDocument}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Yine de Devam Et (Ödeme Alındıysa Satışı Kaydet)
          </Button>
        )}

        {/* Belgeyi İptal Et - Sadece Ingenico 5000F için (kağıt hatası hariç) */}
        {isIngenico && onCancelDocument && !isPaperOutError && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={isCancellingDocument ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            onClick={handleCancelDocument}
            disabled={isCancellingDocument}
            sx={{
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              '&:hover': { bgcolor: 'warning.dark' },
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {isCancellingDocument ? 'İptal Ediliyor...' : 'Belgeyi İptal Et'}
          </Button>
        )}

        {/* Yeniden Dene */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          disabled={isCancellingDocument}
          sx={{
            bgcolor: 'success.main',
            '&:hover': { bgcolor: 'success.dark' },
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          Yeniden Dene
        </Button>

        {/* Kapat (Sepet Korunur) */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          startIcon={<CloseIcon />}
          onClick={handleClose}
          disabled={isCancellingDocument}
          sx={{
            borderColor: 'primary.main',
            color: 'primary.main',
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
            '&:hover': {
              borderColor: 'primary.dark',
              bgcolor: 'primary.light',
            },
          }}
        >
          Kapat (Sepet Korunur)
        </Button>

        {/* Sepeti Temizle - Restoran modunda gizle */}
        {!isRestaurantMode && (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<DeleteSweepIcon />}
            onClick={handleCloseAndClearCart}
            disabled={isCancellingDocument}
            sx={{
              borderColor: 'error.main',
              color: 'error.main',
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
              '&:hover': {
                borderColor: 'error.dark',
                bgcolor: 'error.light',
              },
            }}
          >
            Sepeti Temizle ve Kapat
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PosPaymentErrorModal;

